import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Calendar, Check, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { usePortfolioPrefsStore } from '../store';
import { convertAmount } from '../lib/currencyConvert';
import { formatCurrency } from '../lib/utils';
import { monthlyOutgoingsAPI, recurringCostCandidatesAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { COST_TYPE_DEFS } from '../lib/costTaxonomy';

const COST_TYPE_OPTIONS = COST_TYPE_DEFS
  .filter((d) => !d.is_percentage)
  .map((d) => ({ value: d.code, label: d.display_label }));

export function getDefaultMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Shared Monthly Outgoings view. This is the single implementation of the
 * outgoings fetch/render/convert/promote logic. It is consumed by the
 * Property Portfolio costs page and by the Subscriptions surface (both the
 * Entities scope and the Personal scope), so the grouping and money handling
 * live in exactly one place.
 *
 * Scope is controlled by the parent:
 *   - entityId: a single entity slug, or the literal 'all' to span the scope.
 *   - scope: 'entities' | 'personal' (only meaningful when entityId === 'all').
 *   - controlSlot: optional node rendered in the controls row (the Property
 *     Portfolio passes its own entity <select>; the scoped surfaces pass none
 *     because their scope comes from the global entity selector).
 *
 * When entityId === 'all', an Entity column is shown per row. Amounts stay
 * signed in the data (backend filters amount < 0); Math.abs is applied only at
 * display/prefill time.
 */
export function MonthlyOutgoingsView({
  eurGbpRate,
  entityId,
  scope = 'entities',
  controlSlot = null,
  // The "Covered / Not in P&L" status only means something on the Property
  // Portfolio, where a cost row is what puts a payment in the portfolio P&L.
  // The Entities/Personal P&L is cash-based (every bank outgoing is in it),
  // so the Subscriptions pages hide it (Fan, 2026-10-29).
  uncoveredLabel = 'Not in P&L',
}) {
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const [month, setMonth] = useState(getDefaultMonth());
  const [transactions, setTransactions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);

  const allMode = entityId === 'all';
  // All-entities mode only: a client-side entity filter over the annotated
  // rows. Single-entity mode keeps NO filter control (per the original spec).
  const [entityFilter, setEntityFilter] = useState('all');

  // Promote inline state
  const [promoteId, setPromoteId] = useState(null);
  const [promoteForm, setPromoteForm] = useState(null);
  const [promoteLoading, setPromoteLoading] = useState(false);

  // One-off inline state
  const [oneOffId, setOneOffId] = useState(null);
  const [oneOffForm, setOneOffForm] = useState(null);
  const [oneOffLoading, setOneOffLoading] = useState(false);

  const fetchOutgoings = useCallback(async () => {
    if (!entityId || !month) return;
    setLoading(true);
    try {
      const { data } = await monthlyOutgoingsAPI.list({ entity_id: entityId, month, scope });
      setTransactions(data.transactions ?? []);
      setMeta({ entity_name: data.entity_name, month: data.month });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [entityId, month, scope]);

  useEffect(() => { fetchOutgoings(); }, [fetchOutgoings]);

  const cv = (val, fromCurrency) => convertAmount(val, fromCurrency, displayCurrency, eurGbpRate);

  const doIgnore = async (txId) => {
    try {
      await monthlyOutgoingsAPI.ignore(txId, {});
      toast.success('Transaction ignored');
      await fetchOutgoings();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const openPromote = (tx) => {
    setPromoteId(tx.id);
    setOneOffId(null);
    setPromoteForm({
      entity_id: tx.entity_id,
      cost_type: 'subscription',
      amount: Math.abs(parseFloat(tx.amount) || 0),
      currency: tx.currency || 'GBP',
      frequency: 'monthly',
      effective_from: tx.date ? tx.date.slice(0, 10) : '',
      notes: tx.description || '',
      normalized_label: tx.normalized_label,
    });
  };

  const doPromote = async () => {
    if (!promoteForm) return;
    setPromoteLoading(true);
    try {
      // Write it as an entity-level cost row.
      await monthlyOutgoingsAPI.addOneOff({
        entity_id: promoteForm.entity_id,
        property_id: null,
        cost_type: promoteForm.cost_type,
        amount: promoteForm.amount,
        currency: promoteForm.currency,
        date: promoteForm.effective_from,
        notes: `Recurring: ${promoteForm.notes}`,
        source_transaction_id: promoteId,
      });
      // Also promote a matching recurring cost candidate if one exists.
      const { data: candidateData } = await recurringCostCandidatesAPI.list({
        entity_id: promoteForm.entity_id,
        status: 'new',
      });
      const matchingCandidate = candidateData.candidates?.find(
        (c) => c.normalized_label === promoteForm.normalized_label
      );
      if (matchingCandidate) {
        await recurringCostCandidatesAPI.promote(matchingCandidate.id, {
          property_id: null,
          cost_type: promoteForm.cost_type,
          amount: promoteForm.amount,
          currency: promoteForm.currency,
          frequency: promoteForm.frequency,
          effective_from: promoteForm.effective_from,
        });
      }
      toast.success('Promoted to recurring cost');
      setPromoteId(null);
      setPromoteForm(null);
      await fetchOutgoings();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPromoteLoading(false);
    }
  };

  const openOneOff = (tx) => {
    setOneOffId(tx.id);
    setPromoteId(null);
    setOneOffForm({
      entity_id: tx.entity_id,
      cost_type: 'other_expense',
      amount: Math.abs(parseFloat(tx.amount) || 0),
      currency: tx.currency || 'GBP',
      date: tx.date ? tx.date.slice(0, 10) : '',
      notes: tx.description || '',
    });
  };

  const doOneOff = async () => {
    if (!oneOffForm) return;
    setOneOffLoading(true);
    try {
      await monthlyOutgoingsAPI.addOneOff({
        entity_id: oneOffForm.entity_id,
        property_id: null,
        cost_type: oneOffForm.cost_type,
        amount: oneOffForm.amount,
        currency: oneOffForm.currency,
        date: oneOffForm.date,
        notes: oneOffForm.notes,
        source_transaction_id: oneOffId,
      });
      toast.success('One-off cost added');
      setOneOffId(null);
      setOneOffForm(null);
      await fetchOutgoings();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setOneOffLoading(false);
    }
  };

  const baseTxns = showIgnored
    ? transactions
    : transactions.filter((t) => !t.is_ignored);

  // Distinct entities present in the rows (for the all-entities filter).
  const entityOptions = useMemo(() => {
    const seen = new Map();
    for (const t of transactions) {
      if (t.entity_id && !seen.has(t.entity_id)) {
        seen.set(t.entity_id, t.entity_name || t.entity_id);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [transactions]);

  const visibleTxns = (allMode && entityFilter !== 'all')
    ? baseTxns.filter((t) => t.entity_id === entityFilter)
    : baseTxns;

  // colSpan accounts for the optional Entity column in all-entities mode.
  const inlineColSpan = allMode ? 7 : 6;

  return (
    <div className="space-y-4" data-testid="monthly-outgoings-view">
      {/* Controls row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="outgoings-month-picker"
          />
        </div>
        {controlSlot}
        {allMode && (
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="outgoings-entity-filter"
          >
            <option value="all">All entities</option>
            {entityOptions.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        )}
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={showIgnored}
            onChange={(e) => setShowIgnored(e.target.checked)}
            className="rounded"
          />
          Show ignored
        </label>
        <span className="text-xs text-muted-foreground">
          {visibleTxns.length} transaction{visibleTxns.length !== 1 ? 's' : ''}
          {visibleTxns.filter((t) => t.is_covered).length > 0 &&
            `, ${visibleTxns.filter((t) => t.is_covered).length} covered`}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !entityId ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground text-sm">
              Select an entity and month to view outgoings.
            </p>
          </CardContent>
        </Card>
      ) : visibleTxns.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground text-sm">
              No debit transactions found for this month.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              {meta?.entity_name ?? entityId} - {meta?.month ?? month}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[250px]">Description</TableHead>
                  {allMode && <TableHead>Entity</TableHead>}
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleTxns.map((tx) => (
                  <React.Fragment key={tx.id}>
                    <TableRow
                      className={tx.is_ignored ? 'opacity-40' : ''}
                      data-testid={`outgoing-row-${tx.id}`}
                    >
                      <TableCell className="text-xs">{tx.date?.slice(0, 10)}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[250px] truncate" title={tx.description}>
                        {tx.description}
                      </TableCell>
                      {allMode && (
                        <TableCell className="text-xs">
                          <Badge variant="outline" className="text-[10px]" data-testid={`outgoing-entity-${tx.id}`}>
                            {tx.entity_name || tx.entity_id}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell className="text-xs text-muted-foreground">{tx.account_name || tx.bank_name}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums text-red-600 dark:text-red-400">
                        {formatCurrency(cv(Math.abs(parseFloat(tx.amount) || 0), tx.currency), displayCurrency)}
                      </TableCell>
                      <TableCell>
                        {tx.is_covered && (
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 text-[10px]" data-testid={`badge-covered-${tx.id}`}>
                            Covered
                          </Badge>
                        )}
                        {tx.is_ignored && (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">
                            Ignored
                          </Badge>
                        )}
                        {!tx.is_covered && !tx.is_ignored && (
                          <span className="text-[10px] text-amber-600 dark:text-amber-400" data-testid={`badge-zero-contrib-${tx.id}`}>
                            {uncoveredLabel}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!tx.is_ignored && !tx.is_covered && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openPromote(tx)}
                              className="h-6 px-1.5 text-[10px] font-medium rounded border border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400"
                              title="Promote to recurring"
                              data-testid={`outgoing-promote-${tx.id}`}
                            >
                              <Check className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => openOneOff(tx)}
                              className="h-6 px-1.5 text-[10px] font-medium rounded border border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
                              title="Add as one-off cost"
                              data-testid={`outgoing-oneoff-${tx.id}`}
                            >
                              +1
                            </button>
                            <button
                              onClick={() => doIgnore(tx.id)}
                              className="h-6 px-1.5 text-[10px] font-medium rounded border border-muted text-muted-foreground hover:bg-muted"
                              title="Ignore"
                              data-testid={`outgoing-ignore-${tx.id}`}
                            >
                              <EyeOff className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Promote inline form */}
                    {promoteId === tx.id && promoteForm && (
                      <TableRow>
                        <TableCell colSpan={inlineColSpan} className="bg-muted/30 p-3">
                          <div className="grid grid-cols-3 gap-2 max-w-xl">
                            <select
                              value={promoteForm.cost_type}
                              onChange={(e) => setPromoteForm({ ...promoteForm, cost_type: e.target.value })}
                              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            >
                              {COST_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <select
                              value={promoteForm.frequency}
                              onChange={(e) => setPromoteForm({ ...promoteForm, frequency: e.target.value })}
                              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            >
                              <option value="monthly">Monthly</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="annual">Annual</option>
                            </select>
                            <div className="flex gap-1">
                              <button
                                onClick={doPromote}
                                disabled={promoteLoading}
                                className="h-8 px-2 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              >
                                {promoteLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                              </button>
                              <button
                                onClick={() => { setPromoteId(null); setPromoteForm(null); }}
                                className="h-8 px-2 text-xs font-medium rounded border border-input hover:bg-muted"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* One-off inline form */}
                    {oneOffId === tx.id && oneOffForm && (
                      <TableRow>
                        <TableCell colSpan={inlineColSpan} className="bg-muted/30 p-3">
                          <div className="grid grid-cols-3 gap-2 max-w-xl">
                            <select
                              value={oneOffForm.cost_type}
                              onChange={(e) => setOneOffForm({ ...oneOffForm, cost_type: e.target.value })}
                              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            >
                              {COST_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={oneOffForm.notes}
                              onChange={(e) => setOneOffForm({ ...oneOffForm, notes: e.target.value })}
                              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                              placeholder="Notes"
                            />
                            <div className="flex gap-1">
                              <button
                                onClick={doOneOff}
                                disabled={oneOffLoading}
                                className="h-8 px-2 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {oneOffLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
                              </button>
                              <button
                                onClick={() => { setOneOffId(null); setOneOffForm(null); }}
                                className="h-8 px-2 text-xs font-medium rounded border border-input hover:bg-muted"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
